import { of as observableOf, Observable } from 'rxjs';
import { map, tap } from 'rxjs/operators';
import { Injectable } from '@angular/core';

interface CacheValue<T> {
  cachedAt: Date;
  data: T;
}

const CacheKeyPartSeparator = '|';

@Injectable()
export abstract class LocalCacheService<T> {
  /**
   * Load data when there is a cache miss
   * @param ids A list of the ids that need to be filled in the cache
   */
  protected abstract load(ids: Array<string>): Observable<{ [key: string]: T }>;

  /**
   * How long to keep items in the cache
   */
  protected abstract maxAge(): number;

  /**
   * Namespace of the cache. If this cache fills only keys in this namespace will be cleared
   */
  protected abstract namespace(): string;

  public getMulti(ids: Array<string>): Observable<{ [key: string]: T }> {
    // Look at cache to determine what we have and what we need to get
    const filteredCacheHits = {};
    const resourceIdsToRequest = [];
    ids.forEach((id) => {
      const k = this.cacheKey(id);
      let hit = this.get(k);
      if (hit && this.isHitExpired(hit)) {
        localStorage.removeItem(k);
        hit = null;
      }
      if (hit) {
        filteredCacheHits[this.resourceIdKey(id)] = hit.data;
      } else {
        resourceIdsToRequest.push(id);
      }
    });

    const load$ = resourceIdsToRequest.length ? this.load(resourceIdsToRequest) : observableOf({});
    return load$.pipe(
      tap((loadedData) => {
        const now = new Date();
        // Populate cache
        Object.keys(loadedData).forEach((id) => {
          this.set(this.cacheKey(id), {
            cachedAt: now,
            data: loadedData[id],
          });
        });
      }),
      map((loadedData) => Object.assign(loadedData, filteredCacheHits)),
    );
  }

  private get(k: string): CacheValue<T> {
    const hit = localStorage.getItem(k);
    if (hit) {
      const p = JSON.parse(hit);
      p.cachedAt = new Date(p.cachedAt);
      return p;
    }
  }

  private set(key: string, item: CacheValue<T>): void {
    try {
      localStorage.setItem(key, JSON.stringify(item));
    } catch (error) {
      this.clear();
    }
    return;
  }

  private clear(): void {
    if (this.browserSupport) {
      Object.keys(localStorage)
        .filter((k) => k.startsWith(this.namespace()))
        .map((k) => localStorage.removeItem(k));
    }
  }

  private get browserSupport(): boolean {
    return !!typeof Storage;
  }

  private isHitExpired(hit: CacheValue<T>): boolean {
    const now = new Date();
    return now.valueOf() - hit.cachedAt.valueOf() > this.maxAge();
  }

  private cacheKey(resourceId: string): string {
    return [this.namespace(), this.resourceIdKey(resourceId)].join(CacheKeyPartSeparator);
  }

  private resourceIdKey(resourceId: string): string {
    return resourceId;
  }
}
